import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import BottomNav from '../components/BottomNav';
import useAutoRefresh from '../utils/refresh';

// ── Message rendering: overflow fix + 3-line clamp + tappable links ───────────
// Long unbroken strings (Google Meet links etc.) previously overflowed the card
// horizontally — overflowWrap:'anywhere' breaks them. Collapsed view clamps to
// 3 lines with "Read more" (shown only when actually truncated, measured via
// ref). URLs (https://… or www.…) render as links opening in a new tab; link
// taps never toggle the collapse.
const URL_RE = /((?:https?:\/\/|www\.)[^\s]+)/gi;
const linkify = text => String(text || '').split(URL_RE).map((part, i) => {
  if (i % 2 === 1) {
    const href = part.startsWith('www.') ? `https://${part}` : part;
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'underline' }}>{part}</a>;
  }
  return part;
});

function UpdateMessage({ text, className, style }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) setClamped(ref.current.scrollHeight > ref.current.clientHeight + 1);
  }, [text]);
  return (
    <>
      <div
        ref={ref}
        className={className}
        onClick={() => { if (clamped && !expanded) setExpanded(true); }}
        style={{
          ...style,
          overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap',
          cursor: clamped && !expanded ? 'pointer' : 'default',
          ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
        }}
      >
        {linkify(text)}
      </div>
      {(clamped || expanded) && (
        <button
          onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
          style={{ background: 'none', border: 'none', padding: 0, marginTop: 2, fontSize: 12, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          {expanded ? 'Show less' : 'Read more…'}
        </button>
      )}
    </>
  );
}

const typeIcon = type => {
  if (type === 'urgent') return '⚠️';
  if (type === 'event') return '📅';
  return '📣';
};

const typeBadge = type => {
  if (type === 'urgent') return 'badge-urgent';
  if (type === 'event') return 'badge-event';
  return 'badge-info';
};

const typeLabel = type => {
  if (type === 'urgent') return 'URGENT';
  if (type === 'event') return 'EVENT';
  return 'INFO';
};

const formatDate = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

// Module-scoped (not redefined per render) so an expanded update stays
// expanded when the parent re-renders (mark-all-read, past toggle).
const UpdateCard = ({ u }) => (
  <div
    key={u.id}
    className="update-item"
    style={{
      opacity: u.is_read ? 0.7 : 1,
      borderLeft: !u.is_read ? '3px solid var(--accent)' : '3px solid transparent',
      paddingLeft: 10,
      marginBottom: 2,
    }}
  >
    <div className="update-icon">{typeIcon(u.type)}</div>
    <div className="update-content" style={{ minWidth: 0 }}>
      <div className="update-title" style={{ overflowWrap: 'anywhere' }}>{u.title || 'Update'}</div>
      {/* Event tag — shown prominently when update is event-specific */}
      {u.event_id && u.event_title && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'var(--accent-glow)', border: '1px solid var(--accent)',
          borderRadius: 5, padding: '2px 8px', marginTop: 4, marginBottom: 4,
          fontSize: 11, fontWeight: 700, color: 'var(--accent)',
        }}>
          📅 {u.event_title}
        </div>
      )}
      <UpdateMessage text={u.message} className="update-msg" />
      {u.created_at && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {formatDate(u.created_at)}
        </div>
      )}
    </div>
    <span className={`badge ${typeBadge(u.type)}`} style={{ fontSize: 9, alignSelf: 'flex-start', marginTop: 2 }}>
      {typeLabel(u.type)}
    </span>
  </div>
);

export default function Updates() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  // Refetch when the app returns to the foreground, when BottomNav's poll
  // sees a counter rise, or when the active tab is tapped. See utils/refresh.js.
  const refreshKey = useAutoRefresh();
  useEffect(() => {
    axios.get('/api/staff/updates')
      .then(r => {
        const sorted = [...r.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setUpdates(sorted);
      })
      .finally(() => setLoading(false));
    // ⚠️ A refresh re-reads is_read from the server, which is the truth.
    // markAllRead's local optimistic update is replaced by the same values.
  }, [refreshKey]);

  const markAllRead = async () => {
    await axios.post('/api/staff/updates/read');
    setUpdates(u => u.map(x => ({ ...x, is_read: 1 })));
  };

  const now = Date.now();

  // Active: unread OR read within last 3 days
  const active = updates.filter(u =>
    !u.is_read || (now - new Date(u.created_at).getTime()) < THREE_DAYS_MS
  );

  // Past: read AND older than 3 days
  const past = updates.filter(u =>
    u.is_read && (now - new Date(u.created_at).getTime()) >= THREE_DAYS_MS
  );

  return (
    <div className="app-shell">
      <div className="screen">
        <div className="topbar">
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px' }}>Updates</h2>
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 4, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Centralised Communication</div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : updates.length === 0 ? (
            <div className="empty-state">No updates yet.</div>
          ) : (
            <>
              {/* Active updates */}
              {active.length === 0 ? (
                <div className="empty-state" style={{ fontSize: 13 }}>You're all caught up.</div>
              ) : (
                active.map((u, i) => (
                  <UpdateCard key={u.id} u={u} i={i} />
                ))
              )}

              {/* Past updates dropdown */}
              {past.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <button
                    onClick={() => setShowPast(v => !v)}
                    style={{
                      width: '100%', display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', background: 'var(--bg-surface)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      padding: '10px 14px', cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
                    }}
                  >
                    <span>📂 Past Updates ({past.length})</span>
                    <span style={{ fontSize: 11, transition: 'transform 0.2s', display: 'inline-block', transform: showPast ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </button>
                  {showPast && (
                    <div style={{ marginTop: 8, borderLeft: '2px solid var(--border)', paddingLeft: 8 }}>
                      {past.map((u, i) => (
                        <UpdateCard key={u.id} u={u} i={i} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '4px 16px 12px' }}>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            *Review updates daily for shifts and roles. All communications are central. Action required where indicated.*
          </p>
        </div>
      </div>

      {active.some(u => !u.is_read) && (
        <div style={{ position: 'fixed', bottom: 'calc(64px + env(safe-area-inset-bottom))', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 428, padding: '0 16px', zIndex: 15 }}>
          <button className="btn btn-primary" onClick={markAllRead}>
            Acknowledge &amp; Mark All Read
          </button>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
